import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  ButtonVariant,
  Icon,
  IconNames
} from "@wattpad/web-ui-library";
import classNames from "classnames";
import RemovePollModal from "./RemovePollModal";
import { useTrans } from "../../../hooks";
import postPoll from "../../../../helpers/client.post-poll"; 
import getPoll from "../../../../helpers/polls/client.get-writer-poll"; 
import ReaderPoll from "../../../shared-components/ReaderPoll";

export const POLL_STATE = {
  ADD_POLL: 1,        
  EDITING_POLL: 2,   
  VIEW_POLL: 3
 };

const WriterPoll = ({storyId, partId}) => {
  const { trans } = useTrans();
  const [pollState, setPollState] = useState();
  const [pollOptions, setPollOptions] = useState([
    { label: "Option A" },
    { label: "Option B" }
  ]);
  const [pollIsValid, setPollIsValid] = useState(false);
  const [pollTitle, setPollTitle] = useState();
  const [showRemoveModal, setShowRemoveModal] = useState()
  const [viewPollData, setViewPollData] = useState(); 

  useEffect(() => {
    getPoll(storyId, partId).then(data => {
      if (data.polls.length > 0) {
        setPollState(POLL_STATE.VIEW_POLL)
        setViewPollData(data); 
      } else {
        setPollState(POLL_STATE.ADD_POLL)
      }
    })
  },[])

  useEffect(
    () => {
      let valid = true;
      for (let i = 0; i < pollOptions.length; i++) {
        if (!pollOptions[i].valid) {
          valid = false;
        }
      }
      setPollIsValid(valid && pollTitle?.length >= 3);
    },
    [pollOptions, pollTitle]
  );

  const onPollOptionChange = (option, value) => {
    let newPollOptions = pollOptions.slice();
    newPollOptions[option] = {
      label: newPollOptions[option].label,
      value: value,
      valid: value.length >= 1
    };
    setPollOptions(newPollOptions);
  };

  const onPollOptionRemove = option => {
    let newPollOptions = pollOptions.slice();
    newPollOptions.splice(option, 1);
    setPollOptions(newPollOptions);
  };

  const handleDiscardPoll = () => {
      setShowRemoveModal({
        title: trans("Discard poll?"),
        subTitle: trans("Your poll will be permanently removed"),
        label: trans("Discard"),
        onRemove: () => {
          setPollState(POLL_STATE.ADD_POLL);
          setPollOptions([
            { label: "Option A" },
            { label: "Option B" }
          ])
          setPollTitle("");
          setPollIsValid(false);
        }
      });
  }

  const handleAddPoll = () => {
    const options = pollOptions.map((option) => ({text: option.value}));
    postPoll(storyId, partId, "singleAnswerPoll", pollTitle, options).then(data => {
      setPollState(POLL_STATE.VIEW_POLL)
      setViewPollData(data); 
    })
  }

  if (pollState === POLL_STATE.ADD_POLL) {
    return (
      <Button
        variant={ButtonVariant.SECONDARY}
        label={trans("Add a poll")}
        ariaLabel={trans("Add a poll")}
        leadingIcon={IconNames.WpAddPoll}
        onClick={() => setPollState(POLL_STATE.EDITING_POLL)}
      />
    );
  } else if (pollState === POLL_STATE.EDITING_POLL) {
    return (
      <>
      {showRemoveModal && 
        <RemovePollModal 
          title={showRemoveModal?.title}
          subTitle={showRemoveModal?.subTitle}
          removeLabel={showRemoveModal?.label}
          onRemove={showRemoveModal?.onRemove}
          onClose={() => setShowRemoveModal()}
      />}
      <div className="writer-poll-container">
        <textarea
          className="writer-poll-title"
          type="text"
          id="name"
          name="name"
          placeholder="Ask your readers a question"
          maxLength="200"
          onChange={event => setPollTitle(event.target.value)}
        />
        {pollOptions?.map((option, index) => (
          <div className="input-wrapper" key={`option-${index}`}>
            <input
              className={classNames("writer-poll-option", {
                "valid-option": option?.valid
              })}
              type="text"
              id={`option-${index}`}
              name={`option-${index}`}
              placeholder={option.label}
              value={option?.value || ""}
              onChange={event => onPollOptionChange(index, event.target.value)}
              maxLength="140"
            />
            {pollOptions.length > 2 && (
              <button onClick={() => onPollOptionRemove(index)}>
                <Icon name={IconNames.WpClose} size={20} />
              </button>
            )}
          </div>
        ))}
        {pollOptions.length < 5 && (
          <div className="add-option-button">
          <Button
            variant={ButtonVariant.SECONDARY}
            label={trans("Add another answer")}
            ariaLabel={trans("Add another answer")}
            leadingIcon={IconNames.WpAdd}
            onClick={() =>
              setPollOptions([...pollOptions, { label: "Answer" }])
            }
            fullWidth
          />
          </div>
        )}
        <div className="poll-buttons">
          <Button
            variant={ButtonVariant.PRIMARY}
            label={trans("Add poll")}
            disabled={!pollIsValid}
            ariaLabel={trans("Add poll")}
            onClick={() => handleAddPoll()}
          />
          <Button
            variant={ButtonVariant.TERTIARY}
            label={trans("Discard poll")}
            ariaLabel={trans("Discard poll")}
            onClick={handleDiscardPoll}
          />
        </div>
      </div>
      </>
    );
  } else if (pollState === POLL_STATE.VIEW_POLL) {
    return (
      <div className="view-poll">
      <ReaderPoll 
        storyId={Number(storyId)}
        partId={partId}
        pollData={viewPollData}
      />
      </div>
    )
  } else {
    return <></>
  }
};

WriterPoll.propTypes = {
  storyId: PropTypes.string,
  partId: PropTypes.number
};

export default WriterPoll;
